// src/pages/Customer/CustomerDeletedPage.jsx
import React, { useEffect, useState, useCallback } from "react";
import { Card, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import PaginatedTable from "../Common/PaginatedTable";
import { formatDateTime } from "../../utils/formatters";
import {
  getDeletedCustomersPaged,
  restoreCustomer,
} from "../../services/customerService";
import EntityDetailsSidebar from "../Common/EntityDetailsSidebar";
import usePermission from "../../hooks/usePermission";
import Unauthorized from "../Common/Unauthorized";
import {
  UserX,
  Eye,
  IdCard,
  Mail,
  CalendarClock,
  RotateCcw,
} from "lucide-react";

/**
 * Página: Listado de clientes eliminados lógicamente.
 * - Permite búsqueda por nombre, email, documento o empresa.
 * - Muestra quién y cuándo los eliminó.
 * - Si el usuario tiene permiso, puede restaurar un cliente.
 */
const CustomerDeletedPage = () => {
  const hasPermission = usePermission();

  // Estado tabla
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  // Sidebar (detalle)
  const [selectedItem, setSelectedItem] = useState(null);

  // Esquema para detalles
  const schema = [
    { label: "Nombre completo", key: "fullName" },
    { label: "Email", key: "email" },
    { label: "Tipo cliente", key: "customerTypeName" },
    { label: "Documento", key: "documentTypeCode" },
    { label: "Número", key: "documentNumber" },
    { label: "Empresa (tenant)", key: "companyDisplayName" },
    { label: "Eliminado por", key: "deletedBy" },
    { label: "Eliminado el", key: "deletedOn", render: formatDateTime },
  ];

  // Cargar data
  const fetchDeleted = useCallback(async (page, size, search = "") => {
    try {
      const resp = await getDeletedCustomersPaged(page, size, search);
      if (resp?.success) {
        setRows(resp.data.items || []);
        setTotalRows(resp.data.totalCount || 0);
      } else {
        throw new Error(resp?.message || "Respuesta no exitosa");
      }
    } catch {
      Swal.fire(
        "Error",
        "No se pudieron cargar los clientes eliminados.",
        "error"
      );
    }
  }, []);

  useEffect(() => {
    fetchDeleted(currentPage, perPage, searchTerm);
  }, [fetchDeleted, currentPage, perPage, searchTerm]);

  // Paginación y búsqueda
  const handlePageChange = (page) => setCurrentPage(page);
  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  // Verificación de permiso de vista
  if (!hasPermission("clientes.view_deleted")) return <Unauthorized />;

  // Restaurar cliente (solo si se tiene permiso)
  const handleRestore = async (row) => {
    const result = await Swal.fire({
      title: "¿Restaurar cliente?",
      text: "El cliente y su usuario asociado serán reactivados.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, restaurar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      const resp = await restoreCustomer(row.id);
      if (resp?.success) {
        Swal.fire(
          "Restaurado",
          resp.message || "El cliente fue restaurado correctamente.",
          "success"
        );

        // Refrescar la tabla y cerrar detalles
        fetchDeleted(currentPage, perPage, searchTerm);
        if (selectedItem?.id === row.id) setSelectedItem(null);
      } else {
        // Mostrar el mensaje del backend aunque success sea false
        Swal.fire(
          "Atención",
          resp?.message || "No se pudo restaurar el cliente.",
          "warning"
        );
      }
    } catch (err) {
      // Capturar error con su mensaje
      const backendMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Ocurrió un error inesperado.";

      Swal.fire("Error", backendMsg, "error");
    }
  };

  // Columnas de tabla
  const columns = [
    {
      name: "Nombre",
      selector: (row) => row.fullName || "—",
      sortable: true,
      wrap: true,
    },
    {
      name: "Email",
      selector: (row) => row.email || "—",
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <Mail size={16} className="text-muted" />
          <span>{row.email || "—"}</span>
        </div>
      ),
    },
    {
      name: "Documento",
      selector: (row) => row.documentNumber,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <IdCard size={16} className="text-muted" />
          <span>
            {row.documentTypeCode || "—"} {row.documentNumber || ""}
          </span>
        </div>
      ),
    },
    {
      name: "Eliminado el",
      selector: (row) => row.deletedOn,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <CalendarClock size={16} className="text-muted" />
          <span>{formatDateTime(row.deletedOn)}</span>
        </div>
      ),
      width: "280px",
      center: true,
    },
    {
      name: "Acciones",
      center: true,
      width: "170px",
      cell: (row) => (
        <div className="d-flex gap-2">
          {/* Ver detalles */}
          <Button
            variant="outline-info"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedItem(row);
            }}
            title="Ver detalles"
          >
            <Eye size={16} />
          </Button>

          {/* Restaurar (solo si tiene permiso) */}
          {hasPermission("clientes.restore") && (
            <Button
              variant="outline-success"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleRestore(row);
              }}
              title="Restaurar cliente"
            >
              <RotateCcw size={16} />
            </Button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <div className="container-fluid mt-4 px-4 w-100">
      <Card className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0 text-dark d-flex align-items-center gap-2">
            <UserX size={22} /> Clientes eliminados
          </h4>
        </div>

        <PaginatedTable
          keyField="id"
          columns={columns}
          data={rows}
          totalRows={totalRows}
          perPage={perPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePerRowsChange}
          onSearch={handleSearch}
        />

        <EntityDetailsSidebar
          isVisible={!!selectedItem}
          onClose={() => setSelectedItem(null)}
          title="Detalles del cliente eliminado"
          icon={<UserX size={20} />}
          data={selectedItem}
          schema={schema}
        />
      </Card>
    </div>
  );
};

export default CustomerDeletedPage;
